import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  User,
  Mail,
  Lock,
  Save,
  Eye,
  EyeOff,
  X,
  CheckCircle,
  XCircle,
  Clock,
  Shield,
  Camera,
  Activity,
  AlertCircle,
  Building2,
  Briefcase,
  MapPin,
  Phone,
  Hash,
  Calendar,
  UserCircle,
  PhoneCall
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import Input from '../components/ui/input';
import Label from '../components/ui/label';
import { updateProfile } from '../api/users';
import { getCurrentUser } from '../api/auth';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const Profile = () => {
  const navigate = useNavigate();
  const { user: authUser, checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [emailVerified, setEmailVerified] = useState(false);
  const fileInputRef = useRef(null);
  const profileSectionRef = useRef(null);
  /** Cap Activity Log height to profile column on lg+ (grid would otherwise grow with taller sidebar). */
  const [activityColumnHeight, setActivityColumnHeight] = useState(undefined);

  // Sync Activity Log column height to profile column (lg+). Deps exclude activityLog — it lives in the other column; ResizeObserver handles size changes.
  useLayoutEffect(() => {
    const el = profileSectionRef.current;
    if (!el) return;

    const LG = 1024;

    const syncHeight = () => {
      if (typeof window !== 'undefined' && window.innerWidth < LG) {
        setActivityColumnHeight(undefined);
        return;
      }
      setActivityColumnHeight(el.offsetHeight);
    };

    syncHeight();
    const ro = new ResizeObserver(syncHeight);
    ro.observe(el);
    window.addEventListener('resize', syncHeight);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', syncHeight);
    };
  }, [authUser, loading]);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    // Employee-specific fields
    employee_id: '',
    department: '',
    job_title: '',
    employment_status: 'Active',
    date_of_joining: '',
    work_location: '',
    personal_email: '',
    phone_number: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    // Password fields
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const isEmployee = authUser?.role === 'employee';
  const isAdmin = authUser?.role === 'admin';
  const canEditEmployeeFields = isAdmin; // Only admins can edit employee fields

  const [errors, setErrors] = useState({});
  const [activityLog, setActivityLog] = useState([]);

  useEffect(() => {
    // Load current user data
    const loadUserData = async () => {
      try {
        const response = await getCurrentUser();
        if (response.success) {
          const userData = response.data;
          setFormData({
            name: userData.name || '',
            email: userData.email || '',
            company: userData.company || '',
            phone_number: userData.mobile_phone || userData.phone_number || '',
            // Employee fields (fallback to empty if not in DB yet)
            employee_id: userData.employee_code || userData.employee_id || '',
            department: userData.department || '',
            job_title: userData.job_title || '',
            employment_status: userData.employment_status || userData.status || 'Active',
            date_of_joining: userData.date_of_joining || userData.created_at || '',
            work_location: userData.office_location || userData.work_location || '',
            personal_email: userData.personal_email || '',
            emergency_contact_name: userData.emergency_contact_name || '',
            emergency_contact_phone: userData.emergency_contact_phone || '',
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
          });
          
          // Load profile image from localStorage if exists
          const savedImage = localStorage.getItem(`profile_image_${response.data.id}`);
          if (savedImage) {
            setProfileImagePreview(savedImage);
          }
          
          // Check email verification status (mock for now, can be from API)
          setEmailVerified(response.data.email_verified || false);
          
          // Load activity log from localStorage
          const savedActivity = localStorage.getItem(`activity_log_${response.data.id}`);
          if (savedActivity) {
            setActivityLog(JSON.parse(savedActivity));
          } else {
            // Initialize with default activities
            const defaultActivities = [
              {
                id: 1,
                action: 'Account Created',
                timestamp: response.data.created_at,
                type: 'account'
              }
            ];
            setActivityLog(defaultActivities);
            localStorage.setItem(`activity_log_${response.data.id}`, JSON.stringify(defaultActivities));
          }
        }
      } catch (error) {
        console.error('Failed to load user data:', error);
        toast.error('Failed to load profile data');
      }
    };

    loadUserData();
  }, []);

  // Calculate password strength
  useEffect(() => {
    if (formData.newPassword) {
      let strength = 0;
      const password = formData.newPassword;
      
      // Length check
      if (password.length >= 8) strength += 1;
      if (password.length >= 12) strength += 1;
      
      // Character variety checks
      if (/[a-z]/.test(password)) strength += 1;
      if (/[A-Z]/.test(password)) strength += 1;
      if (/[0-9]/.test(password)) strength += 1;
      if (/[^a-zA-Z0-9]/.test(password)) strength += 1;
      
      setPasswordStrength(Math.min(strength, 5));
    } else {
      setPasswordStrength(0);
    }
  }, [formData.newPassword]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Phone: required so we can contact you (all roles)
    const phone = (formData.phone_number || '').trim();
    if (!phone) {
      newErrors.phone_number = 'Phone number is required so we can contact you';
    } else if (phone.length < 5) {
      newErrors.phone_number = 'Please enter a valid phone number';
    }

    // Employee-specific validation
    if (isEmployee) {
      if (!formData.department.trim()) {
        newErrors.department = 'Department is required';
      }
      if (!formData.job_title.trim()) {
        newErrors.job_title = 'Job title is required';
      }
      if (formData.personal_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.personal_email)) {
        newErrors.personal_email = 'Invalid email format';
      }
    }

    // If new password is provided, validate password fields
    if (formData.newPassword) {
      if (!formData.currentPassword) {
        newErrors.currentPassword = 'Current password is required to change password';
      }

      if (formData.newPassword.length < 6) {
        newErrors.newPassword = 'Password must be at least 6 characters';
      }

      if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const updateData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
      };

      // All users: company, job_title, phone
      updateData.company = (formData.company || '').trim() || null;
      updateData.job_title = (formData.job_title || '').trim() || null;
      updateData.phone_number = (formData.phone_number || '').trim() || null;

      // Add employee fields if user is employee and has permission to edit
      if (isEmployee && canEditEmployeeFields) {
        updateData.department = formData.department.trim();
        updateData.job_title = formData.job_title.trim();
        updateData.employment_status = formData.employment_status;
        updateData.work_location = formData.work_location.trim();
        updateData.personal_email = formData.personal_email.trim();
        updateData.phone_number = formData.phone_number.trim();
        updateData.emergency_contact_name = formData.emergency_contact_name.trim();
        updateData.emergency_contact_phone = formData.emergency_contact_phone.trim();
      }

      // Only include password fields if new password is provided
      if (formData.newPassword) {
        updateData.currentPassword = formData.currentPassword;
        updateData.newPassword = formData.newPassword;
      }

      const response = await updateProfile(updateData);

      if (response.success) {
        toast.success('Profile updated successfully');
        
        // Save profile image if uploaded
        if (profileImagePreview && authUser?.id) {
          localStorage.setItem(`profile_image_${authUser.id}`, profileImagePreview);
        }
        
        // Add activity log entry
        if (authUser?.id) {
          const newActivity = {
            id: Date.now(),
            action: 'Profile Updated',
            timestamp: new Date().toISOString(),
            type: 'profile',
            details: 'Profile information was updated'
          };
          const updatedLog = [newActivity, ...activityLog].slice(0, 10); // Keep last 10 activities
          setActivityLog(updatedLog);
          localStorage.setItem(`activity_log_${authUser.id}`, JSON.stringify(updatedLog));
        }
        
        // Refresh auth context
        await checkAuth();
        
        // Clear password fields
        setFormData({
          ...formData,
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setErrors({});
        setPasswordStrength(0);
      } else {
        toast.error(response.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Update profile error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update profile';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Extract company from email domain
  const extractCompanyFromEmail = (email) => {
    if (!email || typeof email !== 'string') {
      return '';
    }

    const parts = email.split('@');
    if (parts.length !== 2) {
      return '';
    }

    const domain = parts[1].toLowerCase();
    // Remove common TLDs
    const domainWithoutTld = domain.replace(/\.(com|net|org|io|co|in|tech|info|biz|us|uk|au|ca)$/i, '');
    
    // Split by dots and capitalize each word
    const words = domainWithoutTld.split('.');
    const companyName = words
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    return companyName || '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };

      // Auto-extract company from email when email changes
      if (name === 'email' && value) {
        const extractedCompany = extractCompanyFromEmail(value);
        // Only auto-fill if company field is empty or if it matches the old extraction
        if (!prev.company || extractCompanyFromEmail(prev.email) === prev.company) {
          updated.company = extractedCompany;
        }
      }

      return updated;
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getRoleDisplay = (role) => {
    const roleMap = {
      admin: 'Administrator',
      employee: 'Employee',
      customer: 'Customer',
    };
    return roleMap[role] || role;
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image size should be less than 2MB');
        return;
      }
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(file);
        setProfileImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setProfileImage(null);
    setProfileImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (authUser?.id) {
      localStorage.removeItem(`profile_image_${authUser.id}`);
    }
    toast.success('Profile picture removed');
  };

  const getPasswordStrengthLabel = (strength) => {
    if (strength === 0) return { text: '', color: '' };
    if (strength <= 2) return { text: 'Weak', color: 'text-red-700 bg-red-100' };
    if (strength === 3) return { text: 'Fair', color: 'text-yellow-700 bg-yellow-100' };
    if (strength === 4) return { text: 'Good', color: 'text-blue-700 bg-blue-100' };
    return { text: 'Strong', color: 'text-green-700 bg-green-100' };
  };

  const getPasswordStrengthColor = (strength) => {
    if (strength <= 2) return 'bg-red-600';
    if (strength === 3) return 'bg-yellow-500';
    if (strength === 4) return 'bg-blue-600';
    return 'bg-green-600';
  };

  const formatActivityTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get status badge styling
  const getStatusBadgeStyle = (status) => {
    const statusLower = status?.toLowerCase() || 'active';
    switch (statusLower) {
      case 'active':
        return 'bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700 animate-pulse';
      case 'on leave':
        return 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700';
      case 'resigned':
      case 'terminated':
        return 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700';
    }
  };

  // Get activity icon based on type
  const getActivityIcon = (type) => {
    switch (type) {
      case 'account':
        return <UserCircle className="w-4.5 h-4.5 text-white" />;
      case 'profile':
        return <User className="w-4.5 h-4.5 text-white" />;
      case 'role':
        return <Shield className="w-4.5 h-4.5 text-white" />;
      case 'status':
        return <Activity className="w-4.5 h-4.5 text-white" />;
      case 'department':
        return <Building2 className="w-4.5 h-4.5 text-white" />;
      default:
        return <Clock className="w-4.5 h-4.5 text-white" />;
    }
  };

  return (
    <DashboardLayout userRole={authUser?.role}>
      <div className="flex flex-col overflow-hidden min-h-full bg-[#93d1ff] dark:bg-slate-950">
        {/* Unified Header with Gradient */}
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 dark:from-blue-800 dark:via-indigo-800 dark:to-purple-800 px-6 py-5 shadow-lg">
          <div className="w-full max-w-none">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-white/20 rounded-xl transition-colors backdrop-blur-sm"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <div className="flex-1 flex items-center gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-2xl font-bold text-white">
                      {isEmployee ? 'Employee Profile' : 'My Profile'}
                    </h1>
                    {/* Role Badge */}
                    <span className="px-3 py-1 rounded-xl bg-white/20 backdrop-blur-sm text-white text-xs font-semibold border border-white/30">
                      {getRoleDisplay(authUser?.role)}
                    </span>
                    {/* Status Badge (for employees) */}
                    {isEmployee && formData.employment_status && (
                      <span className={`px-3 py-1 rounded-xl text-xs font-semibold border ${getStatusBadgeStyle(formData.employment_status)}`}>
                        {formData.employment_status}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-blue-100 mt-1">
                    {isEmployee ? 'Manage your employee profile and information' : 'Manage your account settings'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-950 dark:to-slate-900 p-6">
          <div className="w-full max-w-none">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:items-start">
              {/* Left Column - Profile Header & Form (height drives Activity Log cap on desktop) */}
              <div ref={profileSectionRef} className="lg:col-span-2 space-y-6 min-h-0">
                {/* Profile Header Card - Enhanced with Avatar */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-800 overflow-hidden">
                  <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-6">
                    <div className="flex items-start gap-6">
                      {/* Profile Picture Section - Enhanced */}
                      <div className="relative group">
                        <div className="w-32 h-32 rounded-2xl overflow-hidden border-4 border-white dark:border-slate-900 shadow-xl flex items-center justify-center bg-gradient-to-br from-blue-400 to-purple-500">
                          {profileImagePreview ? (
                            <img 
                              src={profileImagePreview} 
                              alt="Profile" 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-white text-5xl font-bold">
                              {authUser?.name?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          )}
                        </div>
                        {/* Hover overlay with camera icon */}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl flex items-center justify-center">
                          <Camera className="w-8 h-8 text-white" />
                        </div>
                        {(isAdmin || !isEmployee) && (
                          <>
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              className="absolute bottom-2 right-2 bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 p-2.5 rounded-xl shadow-lg hover:bg-blue-50 dark:hover:bg-slate-700 transition-all hover:scale-110 border-2 border-blue-600 dark:border-blue-500"
                              title="Upload profile picture"
                            >
                              <Camera className="w-5 h-5" />
                            </button>
                            {profileImagePreview && (
                              <button
                                onClick={handleRemoveImage}
                                className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-xl shadow-lg hover:bg-red-700 transition-all hover:scale-110"
                                title="Remove profile picture"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </>
                        )}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                      </div>
                      <div className="flex-1 text-white">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h2 className="text-2xl font-bold">{authUser?.name || 'User'}</h2>
                          {emailVerified && (
                            <CheckCircle className="w-5 h-5 text-green-300" title="Email Verified" />
                          )}
                        </div>
                        {isEmployee && formData.job_title ? (
                          <p className="text-blue-100 flex items-center gap-2 mb-1">
                            <Briefcase className="w-4 h-4" />
                            {formData.job_title}
                          </p>
                        ) : (
                          <p className="text-blue-100 flex items-center gap-2 mb-1">
                            <Mail className="w-4 h-4" />
                            {authUser?.email || ''}
                          </p>
                        )}
                        {isEmployee && formData.department && (
                          <p className="text-blue-100 flex items-center gap-2 text-sm">
                            <Building2 className="w-4 h-4" />
                            {formData.department}
                          </p>
                        )}
                        {!isEmployee && authUser?.company && (
                          <p className="text-blue-100 flex items-center gap-2 text-sm">
                            <Building2 className="w-4 h-4" />
                            {authUser.company}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Quick Stats */}
                  <div className="p-6 bg-white dark:bg-slate-900">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                        <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">Account Created</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatDate(authUser?.created_at)}
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
                        <p className="text-xs font-medium text-green-600 dark:text-green-400 mb-1">Account Status</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {authUser?.is_active ? 'Active' : 'Inactive'}
                        </p>
                      </div>
                      {isEmployee && formData.date_of_joining && (
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl p-4 border border-purple-200 dark:border-purple-800">
                          <p className="text-xs font-medium text-purple-600 dark:text-purple-400 mb-1">Date of Joining</p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {formatDate(formData.date_of_joining)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

            {/* Edit Profile Form - Redesigned */}
            <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-800 p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-gray-200 dark:border-slate-800">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Edit Profile Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* 1. Name */}
                <div>
                  <Label htmlFor="name" className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    Name
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    className={`!bg-white dark:!bg-slate-800 !text-gray-900 dark:!text-white border-2 ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-slate-700 focus:border-blue-500'} rounded-xl px-4 py-2.5`}
                    placeholder="Enter your full name"
                  />
                  {errors.name && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1.5 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* 2. Company */}
                <div>
                  <Label htmlFor="company" className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    <Building2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    Company
                  </Label>
                  <Input
                    id="company"
                    name="company"
                    type="text"
                    value={formData.company}
                    onChange={handleChange}
                    className={`!bg-white dark:!bg-slate-800 !text-gray-900 dark:!text-white border-2 ${errors.company ? 'border-red-500' : 'border-gray-300 dark:border-slate-700 focus:border-blue-500'} rounded-xl px-4 py-2.5`}
                    placeholder="Enter your company name (optional)"
                  />
                  {errors.company && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1.5 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.company}
                    </p>
                  )}
                </div>

                {/* 3. Email (from login - read-only) */}
                <div className="md:col-span-2">
                  <Label htmlFor="email" className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    Email (from login)
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    readOnly
                    className="!bg-gray-50 dark:!bg-slate-800 !text-gray-700 dark:!text-gray-300 border-2 border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 cursor-not-allowed"
                    placeholder="Your login email"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Email is set when you log in and cannot be changed here.</p>
                </div>

                {/* 4. Job Title (optional - we can ask) */}
                <div>
                  <Label htmlFor="job_title" className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    <Briefcase className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    Job Title
                  </Label>
                  <Input
                    id="job_title"
                    name="job_title"
                    type="text"
                    value={formData.job_title}
                    onChange={handleChange}
                    className={`!bg-white dark:!bg-slate-800 !text-gray-900 dark:!text-white border-2 ${errors.job_title ? 'border-red-500' : 'border-gray-300 dark:border-slate-700 focus:border-blue-500'} rounded-xl px-4 py-2.5`}
                    placeholder="e.g. Manager, Developer (optional)"
                  />
                  {errors.job_title && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1.5 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.job_title}
                    </p>
                  )}
                </div>

                {/* 5. Phone Number */}
                <div>
                  <Label htmlFor="phone_number" className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    <Phone className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    Phone Number
                  </Label>
                  <Input
                    id="phone_number"
                    name="phone_number"
                    type="tel"
                    value={formData.phone_number}
                    onChange={handleChange}
                    className={`!bg-white dark:!bg-slate-800 !text-gray-900 dark:!text-white border-2 ${errors.phone_number ? 'border-red-500' : 'border-gray-300 dark:border-slate-700 focus:border-blue-500'} rounded-xl px-4 py-2.5`}
                    placeholder="e.g. +91 98765 43210"
                  />
                  {errors.phone_number && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1.5 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.phone_number}
                    </p>
                  )}
                </div>
              </div>

              {/* Employee Details Section - Only for Employees */}
              {isEmployee && (
                <div className="mt-8 pt-8 border-t-2 border-gray-200 dark:border-slate-800">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
                      <Briefcase className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 dark:text-white">Employee Details</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {canEditEmployeeFields ? 'Manage employee information' : 'View-only employee information'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Employee ID - Read-only */}
                    <div>
                      <Label htmlFor="employee_id" className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        <Hash className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        Employee ID
                      </Label>
                      <Input
                        id="employee_id"
                        name="employee_id"
                        type="text"
                        value={formData.employee_id}
                        disabled
                        className="!bg-gray-50 dark:!bg-slate-800 !text-gray-500 dark:!text-gray-400 cursor-not-allowed border-2 border-gray-300 dark:border-slate-700 rounded-xl px-4 py-2.5"
                      />
                    </div>

                    {/* Employment Status */}
                    <div>
                      <Label htmlFor="employment_status" className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        <Activity className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        Employment Status
                      </Label>
                      <select
                        id="employment_status"
                        name="employment_status"
                        value={formData.employment_status}
                        onChange={handleChange}
                        disabled={!canEditEmployeeFields}
                        className={`w-full bg-white dark:bg-slate-800 border-2 ${canEditEmployeeFields ? 'border-gray-300 dark:border-slate-700 focus:border-purple-500' : 'border-gray-200 dark:border-slate-700 cursor-not-allowed'} rounded-xl px-4 py-2.5 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-200 dark:focus:ring-purple-900/20 outline-none transition-all`}
                      >
                        <option value="Active">Active</option>
                        <option value="On Leave">On Leave</option>
                        <option value="Resigned">Resigned</option>
                        <option value="Terminated">Terminated</option>
                      </select>
                    </div>

                    {/* Department */}
                    <div>
                      <Label htmlFor="department" className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        <Building2 className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        Department
                      </Label>
                      <Input
                        id="department"
                        name="department"
                        type="text"
                        value={formData.department}
                        onChange={handleChange}
                        disabled={!canEditEmployeeFields}
                        className={`!bg-white dark:!bg-slate-800 !text-gray-900 dark:!text-white border-2 ${errors.department ? 'border-red-500' : canEditEmployeeFields ? 'border-gray-300 dark:border-slate-700 focus:border-purple-500' : 'border-gray-200 dark:border-slate-700 cursor-not-allowed'} rounded-xl px-4 py-2.5`}
                        placeholder="e.g. Engineering, HR, Sales"
                      />
                      {errors.department && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1.5 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.department}
                        </p>
                      )}
                    </div>

                    {/* Job Title */}
                    <div>
                      <Label htmlFor="job_title" className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        <Briefcase className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        Job Title
                      </Label>
                      <Input
                        id="job_title"
                        name="job_title"
                        type="text"
                        value={formData.job_title}
                        onChange={handleChange}
                        disabled={!canEditEmployeeFields}
                        className={`!bg-white dark:!bg-slate-800 !text-gray-900 dark:!text-white border-2 ${errors.job_title ? 'border-red-500' : canEditEmployeeFields ? 'border-gray-300 dark:border-slate-700 focus:border-purple-500' : 'border-gray-200 dark:border-slate-700 cursor-not-allowed'} rounded-xl px-4 py-2.5`}
                        placeholder="e.g. Senior Developer, Manager"
                      />
                      {errors.job_title && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1.5 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.job_title}
                        </p>
                      )}
                    </div>

                    {/* Date of Joining - Read-only */}
                    {formData.date_of_joining && (
                      <div>
                        <Label htmlFor="date_of_joining" className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                          <Calendar className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                          Date of Joining
                        </Label>
                        <Input
                          id="date_of_joining"
                          name="date_of_joining"
                          type="text"
                          value={formatDate(formData.date_of_joining)}
                          disabled
                          className="!bg-gray-50 dark:!bg-slate-800 !text-gray-500 dark:!text-gray-400 cursor-not-allowed border-2 border-gray-300 dark:border-slate-700 rounded-xl px-4 py-2.5"
                        />
                      </div>
                    )}

                    {/* Work Location */}
                    <div>
                      <Label htmlFor="work_location" className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        <MapPin className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        Work Location
                      </Label>
                      <Input
                        id="work_location"
                        name="work_location"
                        type="text"
                        value={formData.work_location}
                        onChange={handleChange}
                        disabled={!canEditEmployeeFields}
                        className={`!bg-white dark:!bg-slate-800 !text-gray-900 dark:!text-white border-2 ${canEditEmployeeFields ? 'border-gray-300 dark:border-slate-700 focus:border-purple-500' : 'border-gray-200 dark:border-slate-700 cursor-not-allowed'} rounded-xl px-4 py-2.5`}
                        placeholder="e.g. Building A, Floor 3, New York Office"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Contact Information Section - Only for Employees */}
              {isEmployee && (
                <div className="mt-8 pt-8 border-t-2 border-gray-200 dark:border-slate-800">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center">
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 dark:text-white">Contact Information</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Work and emergency contact details</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Work Email - Read-only */}
                    <div>
                      <Label htmlFor="work_email" className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        <Mail className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                        Work Email
                      </Label>
                      <Input
                        id="work_email"
                        name="work_email"
                        type="email"
                        value={formData.email}
                        disabled
                        className="!bg-gray-50 dark:!bg-slate-800 !text-gray-500 dark:!text-gray-400 cursor-not-allowed border-2 border-gray-300 dark:border-slate-700 rounded-xl px-4 py-2.5"
                      />
                    </div>

                    {/* Personal Email */}
                    <div>
                      <Label htmlFor="personal_email" className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        <Mail className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                        Personal Email
                      </Label>
                      <Input
                        id="personal_email"
                        name="personal_email"
                        type="email"
                        value={formData.personal_email}
                        onChange={handleChange}
                        disabled={!canEditEmployeeFields}
                        className={`!bg-white dark:!bg-slate-800 !text-gray-900 dark:!text-white border-2 ${errors.personal_email ? 'border-red-500' : canEditEmployeeFields ? 'border-gray-300 dark:border-slate-700 focus:border-indigo-500' : 'border-gray-200 dark:border-slate-700 cursor-not-allowed'} rounded-xl px-4 py-2.5`}
                        placeholder="personal@example.com"
                      />
                      {errors.personal_email && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1.5 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.personal_email}
                        </p>
                      )}
                    </div>

                    {/* Phone Number */}
                    <div>
                      <Label htmlFor="phone_number" className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        <Phone className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                        Phone Number
                      </Label>
                      <Input
                        id="phone_number"
                        name="phone_number"
                        type="tel"
                        value={formData.phone_number}
                        onChange={handleChange}
                        disabled={!canEditEmployeeFields}
                        className={`!bg-white dark:!bg-slate-800 !text-gray-900 dark:!text-white border-2 ${canEditEmployeeFields ? 'border-gray-300 dark:border-slate-700 focus:border-indigo-500' : 'border-gray-200 dark:border-slate-700 cursor-not-allowed'} rounded-xl px-4 py-2.5`}
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>

                    {/* Emergency Contact Name */}
                    <div>
                      <Label htmlFor="emergency_contact_name" className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        <UserCircle className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                        Emergency Contact Name
                      </Label>
                      <Input
                        id="emergency_contact_name"
                        name="emergency_contact_name"
                        type="text"
                        value={formData.emergency_contact_name}
                        onChange={handleChange}
                        disabled={!canEditEmployeeFields}
                        className={`!bg-white dark:!bg-slate-800 !text-gray-900 dark:!text-white border-2 ${canEditEmployeeFields ? 'border-gray-300 dark:border-slate-700 focus:border-indigo-500' : 'border-gray-200 dark:border-slate-700 cursor-not-allowed'} rounded-xl px-4 py-2.5`}
                        placeholder="Contact person name"
                      />
                    </div>

                    {/* Emergency Contact Phone */}
                    <div>
                      <Label htmlFor="emergency_contact_phone" className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        <PhoneCall className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                        Emergency Contact Phone
                      </Label>
                      <Input
                        id="emergency_contact_phone"
                        name="emergency_contact_phone"
                        type="tel"
                        value={formData.emergency_contact_phone}
                        onChange={handleChange}
                        disabled={!canEditEmployeeFields}
                        className={`!bg-white dark:!bg-slate-800 !text-gray-900 dark:!text-white border-2 ${canEditEmployeeFields ? 'border-gray-300 dark:border-slate-700 focus:border-indigo-500' : 'border-gray-200 dark:border-slate-700 cursor-not-allowed'} rounded-xl px-4 py-2.5`}
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Change Password Section - Hidden for customers (they use OTP only) */}
              {authUser?.role !== 'customer' && (
              <div className="mt-8 pt-8 border-t-2 border-gray-200 dark:border-slate-800">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-600 rounded-xl flex items-center justify-center">
                    <Lock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">Change Password</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Leave password fields empty if you don't want to change your password.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Current Password */}
                  <div>
                    <Label htmlFor="currentPassword" className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Current Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="currentPassword"
                        name="currentPassword"
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={formData.currentPassword}
                        onChange={handleChange}
                        className={`!bg-white dark:!bg-slate-800 !text-gray-900 dark:!text-white border-2 pr-10 ${errors.currentPassword ? 'border-red-500' : 'border-gray-300 dark:border-slate-700 focus:border-blue-500'} rounded-xl px-4 py-2.5`}
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                      >
                        {showCurrentPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {errors.currentPassword && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1.5 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.currentPassword}
                      </p>
                    )}
                  </div>

                  {/* New Password */}
                  <div>
                    <Label htmlFor="newPassword" className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                      New Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        name="newPassword"
                        type={showNewPassword ? 'text' : 'password'}
                        value={formData.newPassword}
                        onChange={handleChange}
                        className={`!bg-white dark:!bg-slate-800 !text-gray-900 dark:!text-white border-2 pr-10 ${errors.newPassword ? 'border-red-500' : 'border-gray-300 dark:border-slate-700 focus:border-blue-500'} rounded-xl px-4 py-2.5`}
                        placeholder="Enter new password (min 6 characters)"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                      >
                        {showNewPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {/* Password Strength Indicator - Fixed Colors */}
                    {formData.newPassword && (
                      <div className="mt-3 p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-800 dark:to-slate-900 rounded-xl border-2 border-gray-300 dark:border-slate-700">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Password Strength:</span>
                          <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${getPasswordStrengthLabel(passwordStrength).color} border-2 ${
                            passwordStrength <= 2 ? 'border-red-500' : 
                            passwordStrength === 3 ? 'border-yellow-500' : 
                            passwordStrength === 4 ? 'border-blue-500' : 
                            'border-green-500'
                          }`}>
                            {getPasswordStrengthLabel(passwordStrength).text}
                          </span>
                        </div>
                        <div className="w-full bg-gray-300 dark:bg-slate-700 rounded-full h-4 mb-3 overflow-hidden shadow-inner">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${getPasswordStrengthColor(passwordStrength)} shadow-md`}
                            style={{ width: `${(passwordStrength / 5) * 100}%` }}
                          />
                        </div>
                        <div className="mt-2 text-xs text-gray-600 dark:text-gray-400 space-y-1">
                          {formData.newPassword.length < 8 && (
                            <div className="flex items-center gap-1.5">
                              <XCircle className="w-3 h-3 text-red-500" />
                              <span>Use at least 8 characters</span>
                            </div>
                          )}
                          {!/[a-z]/.test(formData.newPassword) && (
                            <div className="flex items-center gap-1.5">
                              <XCircle className="w-3 h-3 text-red-500" />
                              <span>Add lowercase letters</span>
                            </div>
                          )}
                          {!/[A-Z]/.test(formData.newPassword) && (
                            <div className="flex items-center gap-1.5">
                              <XCircle className="w-3 h-3 text-red-500" />
                              <span>Add uppercase letters</span>
                            </div>
                          )}
                          {!/[0-9]/.test(formData.newPassword) && (
                            <div className="flex items-center gap-1.5">
                              <XCircle className="w-3 h-3 text-red-500" />
                              <span>Add numbers</span>
                            </div>
                          )}
                          {!/[^a-zA-Z0-9]/.test(formData.newPassword) && (
                            <div className="flex items-center gap-1.5">
                              <XCircle className="w-3 h-3 text-red-500" />
                              <span>Add special characters</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {errors.newPassword && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1.5 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.newPassword}
                      </p>
                    )}
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="md:col-span-2">
                  <Label htmlFor="confirmPassword" className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Confirm New Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`!bg-white dark:!bg-slate-800 !text-gray-900 dark:!text-white border-2 pr-10 ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300 dark:border-slate-700 focus:border-blue-500'} rounded-xl px-4 py-2.5`}
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1.5 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>
              </div>
              )}

              {/* Submit Button */}
              <div className="mt-8 pt-6 border-t-2 border-gray-200 dark:border-slate-800 flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="px-8 py-3 rounded-xl font-semibold transition-all hover:scale-105 shadow-md bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border-2 border-gray-300 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 hover:scale-105 shadow-md bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving Changes...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>

              </div>

              {/* Right Column: same height as profile section on lg+; list scrolls inside */}
              <div
                className="lg:col-span-1 flex flex-col min-h-0 w-full"
                style={
                  typeof activityColumnHeight === 'number'
                    ? { height: activityColumnHeight, minHeight: 0 }
                    : undefined
                }
              >
                <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-800 overflow-hidden flex flex-col h-full min-h-0 flex-1">
                  {/* Header - Fixed */}
                  <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-600 dark:from-purple-700 dark:via-pink-700 dark:to-indigo-700 px-5 py-4 flex-shrink-0">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0 backdrop-blur-sm shadow-sm">
                        <Activity className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-white">Activity Log</h3>
                    </div>
                  </div>

                  {/* Activity List - scrolls within fixed column height */}
                  <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                    {activityLog.length === 0 ? (
                      <div className="text-center py-10">
                        <div className="w-14 h-14 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Activity className="w-7 h-7 text-gray-400 dark:text-gray-500" />
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">No activity yet</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Your account activities will appear here</p>
                      </div>
                    ) : (
                      activityLog.map((activity, index) => (
                        <div
                          key={activity.id}
                          className="relative pl-6 pb-4 border-l-2 border-gray-200 dark:border-slate-700 last:pb-0 last:border-0"
                        >
                          {/* Timeline dot */}
                          <div className="absolute left-[-5px] top-1 w-2.5 h-2.5 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 ring-4 ring-white dark:ring-slate-900" />
                          <div className="p-4 rounded-xl border border-gray-200 dark:border-slate-800 hover:border-purple-400 dark:hover:border-purple-600 hover:bg-gradient-to-br hover:from-purple-50 hover:to-pink-50 dark:hover:from-purple-900/20 dark:hover:to-pink-900/20 transition-all bg-white dark:bg-slate-800 shadow-sm">
                            <div className="flex gap-3">
                              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-md">
                                {getActivityIcon(activity.type)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-gray-900 dark:text-white mb-1.5 leading-tight">{activity.action}</p>
                                {activity.details && (
                                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2.5 leading-relaxed line-clamp-2">{activity.details}</p>
                                )}
                                <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 font-medium">
                                  <Clock className="w-3.5 h-3.5 flex-shrink-0 text-purple-500 dark:text-purple-400" />
                                  <span>{formatActivityTime(activity.timestamp)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Profile;

